import React, { createElement } from 'react';
import ProjectNoteTimelineRecordLayout from './ProjectNoteTimelineRecordLayout';
import ProjectNoteTimelineUpdateForm from './createupdate/ProjectNoteTimelineUpdateForm';
import ProjectSystemNoteTimelineRecordLayout from './ProjectSystemNoteTimelineRecordLayout';
import ProjectCloseoutTimelineRecordLayout from './ProjectCloseoutTimelineRecordLayout';
import ProjectSummaryReviewTimelineRecordLayout from './ProjectSummaryReviewTimelineRecordLayout';
import { TimelineItem, TimelineRegistry } from 'framework/ui/timeline';
import CreateProjectUserNoteForm from './createupdate/CreateProjectUserNoteForm';
import CreateCloseout from './createupdate/CreateCloseout';
import UpdateCloseout from './createupdate/UpdateCloseout';
import useProjectNotesService from './useProjectNotesService';
import type { Data } from 'framework/types';
import { FormFieldsData } from 'framework/form';
import { PROJECT_NOTE_SOURCE } from './constants';

export const PROJECT_SYSTEM_NOTE_SOURCE = 'projectSystemNote';
export const PROJECT_CLOSEOUT_SOURCE = 'projectCloseout';
export const PROJECT_SUMMARY_REVIEW_SOURCE = 'projectSummaryReview';

interface ProjectNoteRegistryOptions {
  refetch?: () => Promise<void>;
}

interface ProjectCloseoutRegistryOptions {
  projectId: string;
  refetch?: () => Promise<void>;
}

const AddProjectNoteForm = (props: any) =>
  createElement(CreateProjectUserNoteForm, {
    ...props,
    onCancel: props.onCancel ?? (() => {}),
  });

const useProjectNoteTimelineRegistry = (options: ProjectNoteRegistryOptions = {}): TimelineRegistry => {
  const { refetch } = options;
  return {
    name: 'Note',
    AddForm: AddProjectNoteForm as TimelineRegistry['AddForm'],
    UpdateForm: ProjectNoteTimelineUpdateForm as unknown as React.ReactElement,
    RecordLayout: ProjectNoteTimelineRecordLayout as unknown as React.ReactElement,
    isApplicable: (item: TimelineItem) => !!(item as Record<string, unknown>)?.[PROJECT_NOTE_SOURCE],
    source: PROJECT_NOTE_SOURCE,
    urlPostfix: 'notes',
    additionalAfterAddSubmit: refetch
      ? (_data: TimelineItem, _parentContext?: Data, _reloadData?: () => void) => {
          refetch();
        }
      : undefined,
  };
};

export const useProjectCloseoutTimelineRegistry = ({
  projectId,
  refetch,
}: ProjectCloseoutRegistryOptions): TimelineRegistry => {
  const AddCloseoutForm = (props: FormFieldsData) => {
    const { createCloseout } = useProjectNotesService();

    const submitFormFunc = (values: FormFieldsData) => createCloseout(projectId, values);

    return createElement(CreateCloseout, {
      projectId,
      onCancel: props.onCancel ?? (() => {}),
      submitFormFunc,
      afterSubmit: props.afterSubmit,
      messageContext: props.messageContext,
    });
  };

  const UpdateCloseoutForm = (props: any) => {
    return createElement(UpdateCloseout, {
      onCancel: props.onCancel ?? (() => {}),
      projectCloseout: props.getDataFunc,
      afterSubmit: props.afterSubmit,
      messageContext: props.messageContext,
    });
  };

  return {
    name: 'Closeout',
    AddForm: AddCloseoutForm as TimelineRegistry['AddForm'],
    UpdateForm: UpdateCloseoutForm as unknown as React.ReactElement,
    RecordLayout: ProjectCloseoutTimelineRecordLayout as unknown as React.ReactElement,
    isApplicable: (item: TimelineItem) => !!(item as Record<string, unknown>)?.[PROJECT_CLOSEOUT_SOURCE],
    source: PROJECT_CLOSEOUT_SOURCE,
    urlPostfix: 'closeout',
    withoutAddFunctionality: false,
    additionalAfterAddSubmit: refetch
      ? (_data: TimelineItem, _parentContext?: Data, _reloadData?: () => void) => {
          refetch();
        }
      : undefined,
  };
};

export const useProjectSystemNoteTimelineRegistry = (): TimelineRegistry => ({
  name: 'SystemNote',
  AddForm: (() => null) as TimelineRegistry['AddForm'],
  UpdateForm: (<div />) as unknown as React.ReactElement,
  RecordLayout: ProjectSystemNoteTimelineRecordLayout as unknown as React.ReactElement,
  isApplicable: (item: TimelineItem) => !!(item as Record<string, unknown>)?.[PROJECT_SYSTEM_NOTE_SOURCE],
  source: PROJECT_SYSTEM_NOTE_SOURCE,
  urlPostfix: 'system-notes',
  withoutAddFunctionality: true,
});

export const useProjectSummaryReviewTimelineRegistry = (): TimelineRegistry => ({
  name: 'SummaryReview',
  AddForm: (() => null) as TimelineRegistry['AddForm'],
  UpdateForm: (<div />) as unknown as React.ReactElement,
  RecordLayout: ProjectSummaryReviewTimelineRecordLayout as unknown as React.ReactElement,
  isApplicable: (item: TimelineItem) => !!(item as Record<string, unknown>)?.[PROJECT_SUMMARY_REVIEW_SOURCE],
  source: PROJECT_SUMMARY_REVIEW_SOURCE,
  urlPostfix: 'summary-review',
  withoutAddFunctionality: true,
});

export default useProjectNoteTimelineRegistry;
